import React, { useEffect, useState } from 'react';
import { ArrowRightIcon, EyeIcon, EyeOffIcon } from 'lucide-react';
import { usePortal } from '../contexts/PortalContext';
import { brand } from '../data/projects';

/**
 * Where a Supabase staff invitation lands. Matches the Staff Access screen —
 * an invited person sets their password here, then continues straight into the
 * dashboard with the role the owner assigned.
 */
const FIELD = 'mt-4 w-full border-0 border-b border-paper/25 bg-transparent pb-3 pr-10 font-display text-xl text-paper placeholder:text-paper/25 focus:border-paper focus:outline-none';
export function StaffSetupPassword() {
  const {
    staffAcceptInvite,
    staffCheckInvite,
    goStaffAccess
  } = usePortal();
  const [checking, setChecking] = useState(true);
  const [linkError, setLinkError] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let active = true;
    void staffCheckInvite().then(result => {
      if (!active) return;
      if (!result.ok) setLinkError(result.error ?? 'This invitation link is no longer valid.');
      setChecking(false);
    });
    return () => {
      active = false;
    };
    // Once, on arrival: the link is read a single time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError('');
    const result = await staffAcceptInvite(password, confirm);
    setBusy(false);
    if (!result.ok) setError(result.error ?? 'Password could not be set.');
    // On success the provider signs them in and moves them to the dashboard.
  }

  // Nothing renders until the invitation has been checked.
  if (checking) return <div className="min-h-screen w-full bg-ink" data-id="element-955" data-mp="2dj1tqp7k5htg" />;
  return <div className="flex min-h-screen w-full items-center justify-center bg-ink px-6 py-20 text-paper" data-id="element-956" data-mp="3mj4c6uebwx51">
      <div className="w-full max-w-md" data-id="element-957" data-mp="3higig0ypu274">
        <img src={brand.lockup} alt="Portal Productions" className="h-8 w-auto brightness-0 invert sm:h-10" data-id="element-958" data-mp="3dhmxnggkl3ig" />

        <p className="label mt-16 text-paper/50" data-id="element-959" data-mp="38jv6wprnvnfu">Staff Access</p>

        <h1 className="mt-7 whitespace-pre-line font-display text-4xl leading-[0.95] tracking-tight sm:text-5xl" data-id="element-960" data-mp="936l165rtl5o">
          {linkError ? 'Invitation\nexpired.' : 'Set your\npassword.'}
        </h1>

        {linkError ? <>
            <p className="label-sm mt-10 leading-relaxed text-paper/50" data-id="element-961" data-mp="9tjxgxjzd8i">{linkError}</p>
            <button type="button" onClick={goStaffAccess} className="label group mt-14 flex w-full items-center justify-between gap-6 border border-paper px-8 py-5 text-paper transition-colors duration-150 ease-out hover:bg-paper hover:text-ink" data-id="element-962" data-mp="3rgcxh3urxhai">
              Back to Staff Access
              <ArrowRightIcon className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover:translate-x-1" strokeWidth={1.25} data-id="element-963" data-mp="vdybz1msbmg7" />
            </button>
          </> : <form onSubmit={submit} className="mt-14" data-id="element-964" data-mp="19c4ostwhelnd">
            <label className="relative block" data-id="element-965" data-mp="19cxzu72h3xbb">
              <span className="label block text-paper/40" data-id="element-966" data-mp="1eb7veis40zxg">New password</span>
              <input type={showPassword ? 'text' : 'password'} value={password} onChange={event => setPassword(event.target.value)} autoComplete="new-password" autoFocus className={FIELD} data-id="element-967" data-mp="1j9sugevmldkb" />
              <button type="button" onClick={() => setShowPassword(current => !current)} aria-label={showPassword ? 'Hide password' : 'Show password'} aria-pressed={showPassword} className="absolute bottom-3 right-0 text-paper/40 transition-colors duration-150 ease-out hover:text-paper/80" data-id="element-968" data-mp="1x17i4z0ha2xp">
                {showPassword ? <EyeOffIcon className="h-4 w-4" strokeWidth={1.25} data-id="element-969" data-mp="1y3mpwskh1xeq" /> : <EyeIcon className="h-4 w-4" strokeWidth={1.25} data-id="element-970" data-mp="1t3s0ktgchfvs" />}
              </button>
            </label>

            <label className="relative mt-10 block" data-id="element-971" data-mp="15fh3m1drs0cs">
              <span className="label block text-paper/40" data-id="element-972" data-mp="11ddp13vt0fna">Confirm password</span>
              <input type={showConfirm ? 'text' : 'password'} value={confirm} onChange={event => setConfirm(event.target.value)} autoComplete="new-password" className={FIELD} data-id="element-973" data-mp="11fgoeeiujl4o" />
              <button type="button" onClick={() => setShowConfirm(current => !current)} aria-label={showConfirm ? 'Hide password' : 'Show password'} aria-pressed={showConfirm} className="absolute bottom-3 right-0 text-paper/40 transition-colors duration-150 ease-out hover:text-paper/80" data-id="element-974" data-mp="2qqh6vfi2yeng">
                {showConfirm ? <EyeOffIcon className="h-4 w-4" strokeWidth={1.25} data-id="element-975" data-mp="3us8qe503h3vl" /> : <EyeIcon className="h-4 w-4" strokeWidth={1.25} data-id="element-976" data-mp="3lp7e2k3u7m7" />}
              </button>
            </label>

            {error && <p role="alert" className="label-sm mt-8 leading-relaxed text-paper" data-id="element-977" data-mp="vb0r9hzi11dq">
                {error}
              </p>}

            <button type="submit" disabled={busy} className="label group mt-14 flex w-full items-center justify-between gap-6 border border-paper px-8 py-5 text-paper transition-colors duration-150 ease-out hover:bg-paper hover:text-ink disabled:border-paper/25 disabled:text-paper/40 disabled:hover:bg-transparent disabled:hover:text-paper/40" data-id="element-978" data-mp="hj429mt4n7lk">
              Set Password
              <ArrowRightIcon className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover:translate-x-1" strokeWidth={1.25} data-id="element-979" data-mp="1jvniihatjthh" />
            </button>
          </form>}

        <p className="label-sm mt-16 max-w-sm leading-relaxed text-paper/25" data-id="element-980" data-mp="19wfyjb9kx0le">
          Portal team accounts only, by invitation. Client access is provided separately by secure
          project link.
        </p>
      </div>
    </div>;
}